import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { reloadCnamRegistrations, addCnamRegistration, updateCnamRegistration } from 'lib/redux/users/slice';
import { UserDataResult } from "interfaces/users";
import { CNAMRegistration } from "interfaces/twilio";
import { RegisteredTwilioNumber } from "interfaces/db";
import { getServicesManager } from 'services';
import { Typography } from 'interfaces/typography';
import { Plus, Search, X, Phone, User, Zap, Save, Clock, CheckCircle, AlertCircle, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { useUserData } from '../../lib/redux/users/user-data-hook';

interface CnamRegistrationProps {
  user: UserDataResult | null;
  onSuccess: () => void;
}

// Temporary CNAM record for preview before saving
interface TempCnamRecord {
  id: string;
  displayName: string;
  createdFor?: UserDataResult; // The user this was created for (if any)
  isCustom: boolean;
  state: 'draft' | 'saving';
}

export function CnamRegistration({ user, onSuccess }: CnamRegistrationProps) {
  const dispatch = useDispatch();
  const cnamState = useSelector((state: RootState) => state.cnamRegistrations);
  const { allUsers } = useUserData();
  
  const [allNumbers, setAllNumbers] = useState<RegisteredTwilioNumber[]>([]);
  const [tempRecords, setTempRecords] = useState<TempCnamRecord[]>([]);
  
  // Get registrations from Redux state
  const registrations = cnamState.value || [];
  const isLoading = cnamState.isLoading;
  const [newDisplayName, setNewDisplayName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [cnamSearchQuery, setCnamSearchQuery] = useState('');
  const [numberSearchQuery, setNumberSearchQuery] = useState('');
  const [assignmentSearchQuery, setAssignmentSearchQuery] = useState('');
  const [selectedNumbers, setSelectedNumbers] = useState<Set<string>>(new Set());
  const [showAssignmentDropdown, setShowAssignmentDropdown] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [expandedRegistrations, setExpandedRegistrations] = useState<Set<string>>(new Set());
  
  // Get users who have CNAM registrations (including temp records)
  const usersWithCnam = new Set([
    // Existing registrations
    ...registrations.map(reg => {
      const matchingUser = allUsers.find(u => 
        u.user_name.toLowerCase().includes(reg.end_user.attributes.cnam_display_name.toLowerCase()) ||
        reg.end_user.attributes.cnam_display_name.toLowerCase().includes(u.user_name.toLowerCase()) ||
        reg.end_user.attributes.cnam_display_name.toLowerCase().includes(u.user_name.split(' ')[0].toLowerCase())
      );
      return matchingUser?.user_id;
    }).filter(Boolean),
    // Temp records
    ...tempRecords.map(temp => temp.createdFor?.user_id).filter(Boolean)
  ]);

  // Users without CNAM registrations - check for exact name matches
  const usersWithoutCnam = allUsers.filter(u => {
    if (!u.parallel_enabled || !u.can_dial || !u.team_is_active) return false;
    if (usersWithCnam.has(u.user_id)) return false;
    if (!u.user_name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    
    // Check if there's already a CNAM with matching first name or full name
    const firstName = u.user_name.split(' ')[0].toLowerCase();
    const fullName = u.user_name.toLowerCase();
    const hasMatchingCnam = registrations.some(reg => {
      const cnamName = reg.end_user.attributes.cnam_display_name.toLowerCase();
      return cnamName === firstName || cnamName === fullName;
    });
    
    return !hasMatchingCnam;
  });
  
  // Filter registrations based on search
  const filteredRegistrations = registrations.filter(reg =>
    reg.end_user.attributes.cnam_display_name.toLowerCase().includes(cnamSearchQuery.toLowerCase())
  );

  // Normalize phone number for consistent comparison (remove +, spaces, dashes)
  const normalizePhoneNumber = (phone: string): string => {
    return phone.replace(/[+\s-]/g, '');
  };
  
  // Get unassigned phone numbers with user info  
  // Create a map of normalized numbers to original CNAM numbers for comparison
  const assignedNumbersNormalized = new Set(
    registrations.flatMap(reg => reg.attached_phone_numbers_e164.map(normalizePhoneNumber))
  );
  
  const unassignedNumbers = allNumbers
    .filter(num => !assignedNumbersNormalized.has(normalizePhoneNumber(num.rep_phone_value)))
    .map(num => {
      const assignedUser = allUsers.find(u => u.user_id === num.agent_id);
      return {
        ...num,
        assignedUser
      };
    })
    .filter(num => {
      if (!numberSearchQuery) return true;
      const query = numberSearchQuery.toLowerCase();
      const phoneMatch = num.rep_phone_value.toLowerCase().includes(query);
      const userMatch = num.assignedUser?.user_name.toLowerCase().includes(query) || false;
      return phoneMatch || userMatch;
    })
    .sort((a, b) => {
      const nameA = a.assignedUser?.user_name || 'Unassigned';
      const nameB = b.assignedUser?.user_name || 'Unassigned';
      return nameA.localeCompare(nameB);
    });

  const loadData = useCallback(async () => {
    if (!user?.team_id) return;
    
    try {
      const [, numbersData] = await Promise.all([
        reloadCnamRegistrations(dispatch, user.team_id),
        getServicesManager().getAllTwilioNumbersAdmin()
      ]);
      
      setAllNumbers(numbersData || []);
    } catch (error) {
      console.error('Error loading CNAM data:', error);
    }
  }, [user?.team_id, dispatch]);

  useEffect(() => {
    if (!user?.team_id) return;
    loadData();
  }, [user?.team_id, loadData]);

  // Create temporary CNAM record for preview
  const handleCreateTempForUser = (targetUser: UserDataResult) => {
    const fullName = targetUser.user_name;
    const tempRecord: TempCnamRecord = {
      id: `temp-${Date.now()}`,
      displayName: fullName,
      createdFor: targetUser,
      isCustom: false,
      state: 'draft'
    };
    setTempRecords(prev => [...prev, tempRecord]);
  };

  const handleCreateTempCustom = () => {
    if (!newDisplayName.trim()) return;
    
    const tempRecord: TempCnamRecord = {
      id: `temp-${Date.now()}`,
      displayName: newDisplayName.trim(),
      isCustom: true,
      state: 'draft'
    };
    setTempRecords(prev => [...prev, tempRecord]);
    setNewDisplayName('');
  };

  // Save temporary record to actual CNAM
  const handleSaveTempRecord = async (tempRecord: TempCnamRecord) => {
    if (!user?.team_id || !tempRecord.displayName.trim()) return;
    
    // Update temp record to saving state
    setTempRecords(prev => prev.map(temp => 
      temp.id === tempRecord.id ? { ...temp, state: 'saving' } : temp
    ));
    
    try {
      const result = await getServicesManager().postCnamRegistration(
        user.team_id,
        tempRecord.displayName.trim()
      );
      
      if (result) {
        // Add to Redux store and remove temp record
        dispatch(addCnamRegistration(result));
        setTempRecords(prev => prev.filter(temp => temp.id !== tempRecord.id));
        onSuccess();
      }
    } catch (error) {
      console.error('Error saving CNAM:', error);
      // Reset temp record state on error
      setTempRecords(prev => prev.map(temp => 
        temp.id === tempRecord.id ? { ...temp, state: 'draft' } : temp
      ));
    }
  };

  const handleDeleteTempRecord = (tempId: string) => {
    setTempRecords(prev => prev.filter(temp => temp.id !== tempId));
  };
  
  const handleUpdateTempRecord = (tempId: string, newDisplayName: string) => {
    setTempRecords(prev => prev.map(temp => 
      temp.id === tempId ? { ...temp, displayName: newDisplayName.slice(0, 15) } : temp
    ));
  };
  
  const toggleRegistrationExpansion = (sid: string) => {
    setExpandedRegistrations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sid)) {
        newSet.delete(sid);
      } else {
        newSet.add(sid);
      }
      return newSet;
    });
  };

  // Handle number selection for bulk assignment
  const handleNumberSelection = (phoneNumber: string, checked: boolean) => {
    setSelectedNumbers(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(phoneNumber);
      } else {
        newSet.delete(phoneNumber);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedNumbers(new Set(unassignedNumbers.map(num => num.rep_phone_value)));
    } else {
      setSelectedNumbers(new Set());
    }
  };

  // Assign selected numbers to a CNAM registration
  const handleAssignToRegistration = async (registration: CNAMRegistration) => {
    if (!user?.team_id || selectedNumbers.size === 0) return;
    
    setIsAssigning(true);
    try {
      // Use the original rep_phone_value format for API call
      const numbersToAssign = Array.from(selectedNumbers);
      const result = await getServicesManager().postCnamRegistration(
        user.team_id,
        registration.end_user.attributes.cnam_display_name,
        numbersToAssign // Add phone numbers to existing registration - these are already in rep_phone_value format
      );
      
      if (result) {
        // Update Redux store
        dispatch(updateCnamRegistration(result));
        setSelectedNumbers(new Set());
        setShowAssignmentDropdown(false);
        setAssignmentSearchQuery(''); // Clear search when assignment is complete
        onSuccess();
      }
    } catch (error) {
      console.error('Error assigning numbers to CNAM:', error);
    } finally {
      setIsAssigning(false);
    }
  };
  
  // Remove number from CNAM registration
  const handleRemoveNumberFromCnam = async (registration: CNAMRegistration, phoneNumber: string) => {
    if (!user?.team_id) return;
    
    try {
      const result = await getServicesManager().postCnamRegistration(
        user.team_id,
        registration.end_user.attributes.cnam_display_name,
        undefined, // no numbers to add
        [phoneNumber] // remove this phone number - phoneNumber is already in correct format from CNAM record
      );
      
      if (result) {
        // Update Redux store
        dispatch(updateCnamRegistration(result));
        onSuccess();
      }
    } catch (error) {
      console.error('Error removing number from CNAM:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'twilio-approved': return <CheckCircle size={16} className="text-green-600" />;
      case 'twilio-rejected': return <AlertCircle size={16} className="text-red-600" />;
      default: return <Clock size={16} className="text-yellow-600" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-600 font-medium">Loading CNAM registrations...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left Side - Current CNAM Records & Custom Creation */}
        <div className="xl:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Typography variant="h2" fontWeight={600} color="#111827">CNAM Records</Typography>
              <Typography variant="smallCaption" color="#6b7280" fontSize="11px">
                Caller ID - takes up to 7 days for approval
              </Typography>
            </div>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search CNAM records..."
                  value={cnamSearchQuery}
                  onChange={(e) => setCnamSearchQuery(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 pr-8"
                />
                <Search size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
              <Typography variant="smallCaption" color="#6b7280" className="bg-green-50 border border-green-200 px-2 py-1 rounded-full">
                {filteredRegistrations.length}/{registrations.length} records
              </Typography>
              {tempRecords.length > 0 && (
                <Typography variant="smallCaption" color="#6b7280" className="bg-blue-50 border border-blue-200 px-2 py-1 rounded-full">
                  {tempRecords.length} draft
                </Typography>
              )}
            </div>
          </div>

          {/* Custom CNAM Input */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-md p-2">
            <div className="flex items-center space-x-2">
              <Typography variant="smallParagraph" fontWeight={500} color="#374151">Custom CNAM:</Typography>
              <input
                type="text"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder="Enter name (max 15 chars)"
                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                maxLength={15}
              />
              <button
                onClick={handleCreateTempCustom}
                disabled={!newDisplayName.trim()}
                className="bg-blue-600 text-white px-2 py-1 text-sm font-medium rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
              >
                <Save size={12} />
                <Typography variant="smallParagraph" color="#ffffff" fontSize="12px">Save</Typography>
              </button>
            </div>
          </div>

          {/* Draft Records */}
          {tempRecords.map((tempRecord) => (
            <div key={tempRecord.id} className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-3 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center border-2 border-blue-200">
                    <User size={16} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="relative">
                      <input
                        type="text"
                        value={tempRecord.displayName}
                        onChange={(e) => handleUpdateTempRecord(tempRecord.id, e.target.value)}
                        className="w-full bg-white border-2 border-dashed border-blue-300 text-lg font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded px-3 py-2 hover:border-blue-400 transition-colors"
                        maxLength={15}
                        placeholder="Enter CNAM name"
                        disabled={tempRecord.state === 'saving'}
                      />
                      <div className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                        <Typography variant="smallCaption" color="#ffffff" fontSize="10px">EDIT</Typography>
                      </div>
                    </div>
                    <Typography variant="smallParagraph" color="#2563eb" fontWeight={500} className="mt-1">
                      {tempRecord.isCustom ? 'Custom CNAM' : `For ${tempRecord.createdFor?.user_name}`} • Click above to edit
                    </Typography>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Typography variant="smallCaption" fontWeight={600} className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 border border-blue-300">
                    {tempRecord.state === 'saving' ? 'Saving...' : 'Draft'}
                  </Typography>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-3 border-t border-blue-200">
                <Typography variant="smallParagraph" color="#1d4ed8">
                  {tempRecord.displayName.trim() ? 'Ready to save this CNAM registration?' : 'Enter a name to save'}
                </Typography>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleDeleteTempRecord(tempRecord.id)}
                    disabled={tempRecord.state === 'saving'}
                    className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                  >
                    <Trash2 size={14} />
                    <Typography variant="smallParagraph" color="#4b5563">Discard</Typography>
                  </button>
                  <button
                    onClick={() => handleSaveTempRecord(tempRecord)}
                    disabled={tempRecord.state === 'saving' || !tempRecord.displayName.trim()}
                    className="px-3 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                  >
                    {tempRecord.state === 'saving' ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <Typography variant="smallParagraph" color="#ffffff">Saving...</Typography>
                      </>
                    ) : (
                      <>
                        <Save size={14} />
                        <Typography variant="smallParagraph" color="#ffffff">Save CNAM</Typography>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Saved Registrations - Scrollable */}
          <div className="max-h-96 overflow-y-auto space-y-2">
            {filteredRegistrations.length > 0 ? (
              filteredRegistrations.map((registration) => {
                const isExpanded = expandedRegistrations.has(registration.end_user.sid);
                return (
                  <div key={registration.end_user.sid} className="bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <User size={16} className="text-gray-600" />
                        </div>
                        <div className="flex-1">
                          <Typography variant="h4" fontWeight={600} color="#111827">
                            {registration.end_user.attributes.cnam_display_name}
                          </Typography>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(registration.trust_product.status)}
                        <Typography variant="smallCaption" fontWeight={500} className={`px-2 py-1 rounded-full text-xs ${
                          registration.trust_product.status === 'twilio-approved' 
                            ? 'bg-green-100 text-green-800' 
                            : registration.trust_product.status === 'twilio-rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {registration.trust_product.status === 'twilio-approved' ? 'Approved' :
                           registration.trust_product.status === 'twilio-rejected' ? 'Rejected' :
                           'Pending'}
                        </Typography>
                        <button
                          onClick={() => toggleRegistrationExpansion(registration.end_user.sid)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors flex items-center space-x-1"
                        >
                          <Typography variant="smallParagraph" color="#2563eb" fontSize="12px">
                            {registration.attached_phone_numbers_e164.length} number{registration.attached_phone_numbers_e164.length !== 1 ? 's' : ''}
                          </Typography>
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        {registration.attached_phone_numbers_e164.length > 0 ? (
                          <div className="space-y-2">
                            {registration.attached_phone_numbers_e164.map((number) => {
                              const assignedUser = allNumbers.find(n => normalizePhoneNumber(n.rep_phone_value) === normalizePhoneNumber(number));
                              const user = assignedUser ? allUsers.find(u => u.user_id === assignedUser.agent_id) : null;
                              return (
                                <div key={number} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 group hover:bg-red-50 transition-colors">
                                  <div className="flex items-center space-x-2">
                                    <Phone size={12} className="text-gray-500" />
                                    <Typography variant="smallParagraph" fontWeight={500} fontSize="12px">
                                      {number}
                                    </Typography>
                                    {user && (
                                      <Typography variant="smallCaption" color="#6b7280" fontSize="11px">
                                        • {user.user_name}
                                      </Typography>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => handleRemoveNumberFromCnam(registration, number)}
                                    className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800 p-1 rounded transition-all"
                                    title="Remove number from CNAM"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-3 text-gray-500 bg-gray-50 rounded border border-dashed border-gray-300">
                            <Typography variant="smallParagraph" color="#6b7280" fontSize="12px">
                              No numbers assigned
                            </Typography>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : registrations.length === 0 && tempRecords.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <User size={32} className="text-gray-400 mx-auto mb-3" />
                <Typography variant="h4" color="#6b7280" fontWeight={500} className="mb-1">
                  No CNAM records yet
                </Typography>
                <Typography variant="smallParagraph" color="#9ca3af">
                  Create your first CNAM registration above
                </Typography>
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <Typography variant="smallParagraph" color="#6b7280">
                  No records match "{cnamSearchQuery}"
                </Typography>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Quick Create & Number Assignment */}
        <div className="space-y-4">
          {/* Quick Create for Users */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-200 rounded-lg p-3 shadow-sm">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <Zap size={16} className="text-white" />
              </div>
              <Typography variant="h3" fontWeight={600} color="#111827">Quick Create</Typography>
            </div>
            
            <div className="relative mb-3">
              <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-8 pr-8 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="space-y-1 max-h-64 overflow-y-auto">
              {usersWithoutCnam.length > 0 ? (
                usersWithoutCnam.map((targetUser) => (
                  <div key={targetUser.user_id} className="flex items-center justify-between px-2 py-1 bg-white rounded border border-gray-200 hover:border-blue-300 transition-colors">
                    <div className="flex items-center space-x-2 flex-1">
                      <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                        <User size={12} className="text-gray-600" />
                      </div>
                      <Typography variant="smallParagraph" fontWeight={500} color="#111827" className="flex-1">
                        {targetUser.user_name}
                      </Typography>
                    </div>
                    <button
                      onClick={() => handleCreateTempForUser(targetUser)}
                      className="bg-blue-600 text-white px-2 py-1 text-xs font-medium rounded hover:bg-blue-700 transition-colors flex items-center space-x-1 ml-2"
                    >
                      <Plus size={12} />
                      <Typography variant="smallParagraph" color="#ffffff" fontSize="11px">Create</Typography>
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <Typography variant="smallParagraph" color="#6b7280">
                    {searchQuery ? `No users match ${searchQuery}` : 'All users have CNAM records'}
                  </Typography>
                </div>
              )}
            </div>
          </div>

          {/* Number Assignment Tool */}
          {allNumbers.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <Typography variant="h3" fontWeight={600} color="#111827">Assign Numbers</Typography>
                <Typography variant="smallCaption" color="#6b7280" className="bg-gray-100 px-2 py-1 rounded-full">
                  {unassignedNumbers.length} unassigned
                </Typography>
              </div>
              
              {/* Search for numbers */}
              <div className="relative mb-3">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by number or user name..."
                  value={numberSearchQuery}
                  onChange={(e) => setNumberSearchQuery(e.target.value)}
                  className="block w-full pl-8 pr-8 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                />
                {numberSearchQuery && (
                  <button
                    onClick={() => setNumberSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Bulk Actions */}
              {selectedNumbers.size > 0 && (
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <Typography variant="smallParagraph" fontWeight={600} color="#1e40af">
                      {selectedNumbers.size} number{selectedNumbers.size !== 1 ? 's' : ''} selected
                    </Typography>
                    <button
                      onClick={() => setSelectedNumbers(new Set())}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      <Typography variant="smallCaption" color="#2563eb">Clear</Typography>
                    </button>
                  </div>
                  
                  <div className="relative">
                    <button
                      onClick={() => setShowAssignmentDropdown(!showAssignmentDropdown)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      disabled={registrations.length === 0 || isAssigning}
                    >
                      <Typography variant="smallParagraph" fontWeight={500} color="#ffffff">
                        {isAssigning ? 'Assigning...' : 'Assign to CNAM'}
                      </Typography>
                      {showAssignmentDropdown ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    
                    {showAssignmentDropdown && registrations.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-64">
                        {/* Search within CNAM records */}
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <Search className="h-3 w-3 text-gray-400" />
                            </div>
                            <input
                              type="text"
                              placeholder="Search CNAM records..."
                              value={assignmentSearchQuery}
                              onChange={(e) => setAssignmentSearchQuery(e.target.value)}
                              className="block w-full pl-7 pr-7 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                            {assignmentSearchQuery && (
                              <button
                                onClick={() => setAssignmentSearchQuery('')}
                                className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="max-h-40 overflow-y-auto">
                          {registrations
                            .filter(reg => 
                              !assignmentSearchQuery || 
                              reg.end_user.attributes.cnam_display_name.toLowerCase().includes(assignmentSearchQuery.toLowerCase())
                            )
                            .map((registration) => (
                          <button
                            key={registration.end_user.sid}
                            onClick={() => handleAssignToRegistration(registration)}
                            disabled={isAssigning}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 disabled:opacity-50"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <Typography variant="smallParagraph" fontWeight={500} color="#111827">
                                  {registration.end_user.attributes.cnam_display_name}
                                </Typography>
                                <Typography variant="smallCaption" color="#6b7280" fontSize="11px">
                                  {registration.attached_phone_numbers_e164.length} number{registration.attached_phone_numbers_e164.length !== 1 ? 's' : ''} assigned
                                </Typography>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                registration.trust_product.status === 'twilio-approved' 
                                  ? 'bg-green-100 text-green-800' 
                                  : registration.trust_product.status === 'twilio-rejected'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {registration.trust_product.status === 'twilio-approved' ? 'Approved' :
                                 registration.trust_product.status === 'twilio-rejected' ? 'Rejected' :
                                 'Pending'}
                              </span>
                            </div>
                          </button>
                          ))}
                          {registrations.filter(reg => 
                            !assignmentSearchQuery || 
                            reg.end_user.attributes.cnam_display_name.toLowerCase().includes(assignmentSearchQuery.toLowerCase())
                          ).length === 0 && assignmentSearchQuery && (
                            <div className="px-3 py-2 text-center">
                              <Typography variant="smallParagraph" color="#6b7280" fontSize="12px">
                                No CNAM records match "{assignmentSearchQuery}"
                              </Typography>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Number List with Selection */}
              {unassignedNumbers.length > 0 ? (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                <div className="flex items-center space-x-2 pb-2 border-b border-gray-200">
                  <input
                    type="checkbox"
                    checked={selectedNumbers.size === unassignedNumbers.length && unassignedNumbers.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <Typography variant="smallParagraph" fontWeight={500} color="#374151">Select All</Typography>
                </div>
                
                {unassignedNumbers.map((number) => (
                  <div key={number.rep_phone_value} className="flex items-center space-x-2 p-2 rounded-md border border-gray-200 hover:border-gray-300 transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedNumbers.has(number.rep_phone_value)}
                      onChange={(e) => handleNumberSelection(number.rep_phone_value, e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <Typography variant="smallParagraph" fontWeight={500} color="#111827" className="font-mono">
                          {number.rep_phone_value}
                        </Typography>
                        <div className="flex items-center space-x-1">
                          <Phone size={12} className="text-gray-400" />
                          <Typography variant="smallCaption" color="#6b7280" fontSize="11px">
                            {number.assignedUser?.user_name || 'Unassigned'}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-md border border-dashed border-gray-300">
                  <Phone size={24} className="text-gray-400 mx-auto mb-2" />
                  <Typography variant="smallParagraph" color="#6b7280" fontWeight={500}>
                    All numbers are assigned to CNAM records
                  </Typography>
                  <Typography variant="smallCaption" color="#9ca3af" fontSize="11px">
                    Remove numbers from CNAM records above to reassign them
                  </Typography>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}